
import React, { useState } from 'react';
import { Box, Grid, Paper, Stack } from '@mui/material';
import { HelpSupportContactCard } from './components/help/HelpSupportContactCard';
import { HelpSupportFaqCard } from './components/help/HelpSupportFaqCard';
import { HelpSupportHeader } from './components/help/HelpSupportHeader';
import { HelpSupportProductGuide } from './components/help/HelpSupportProductGuide';
import { HelpSupportTabs } from './components/help/HelpSupportTabs';

const KnowledgebasePage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [faqItems, setFaqItems] = useState([
    {
      question: 'How do I create a package?',
      answer: 'Go to a client\'s detail page and open the Plans & Packages tab. Click "Create Package", give it a name, add the plans or services you want to include, set the pricing, and save. The package will then be available to assign to that client.',
      expanded: true,
    },
    {
      question: 'How do I reschedule an appointment?',
      answer: 'Open the Appointments page from the sidebar, find the session you need to change, and click on it to view the details. Use the "Reschedule" option to pick a new date and time. The client will be notified of the change. Note that completed or cancelled sessions cannot be rescheduled.',
      expanded: false,
    },
    {
      question: 'How do I set my availability?',
      answer: 'Go to Settings from the sidebar, then select Availability. You can configure your working hours for each day of the week, set time slots, and define break periods. Clients will only be able to book sessions during the times you mark as available.',
      expanded: false,
    },
    {
      question: 'How do clients book sessions?',
      answer: 'Each client has a unique booking link. Open the client\'s detail page, go to the Appointments tab, and click "Copy Booking Link". Share this link with your client — they can use it to view your available time slots and book a session directly without needing an account.',
      expanded: false,
    },
    {
      question: 'How do I create an AI meal plan?',
      answer: 'Navigate to Meal Planning from the sidebar and select "AI Meal Plan". Choose the client, set their calorie and macro targets, specify any allergies, intolerances, cuisine preferences, and medical conditions, then click Generate. The AI will create a personalised multi-day meal plan that you can review, regenerate individual meals, apply smart edits, and finalise.',
      expanded: false,
    },
    {
      question: 'How do I send an invoice to a client?',
      answer: 'Go to the Invoicing page from the sidebar and click "Create Invoice". Select the client, add line items for your services, set the due date, and send. You can configure default payment terms, currency, and tax rates under Settings > Invoice and Tax Details.',
      expanded: false,
    },
    {
      question: 'How do I assign a questionnaire to a client?',
      answer: 'Open the Questionnaires page from the sidebar, select the questionnaire you want to assign, and click "Assign". Choose the client and send it. The client will receive the questionnaire and can fill it out before their session, so you have structured intake data ready when you meet.',
      expanded: false,
    },
  ]);
  const guideCards = [
    {
      badge: 'Profile Setup',
      title: 'Practitioner Profile',
      description: 'Set up your practitioner profile and practice details.',
      videoSrc: '/videos/knowledgebase/practitioner-profile.mp4',
    },
    {
      badge: 'Client Setup',
      title: 'Client Onboarding',
      description: 'Walk through adding a client and completing onboarding.',
      videoSrc: '/videos/knowledgebase/client-onboarding.mp4',
    },
    {
      badge: 'Questionnaires',
      title: 'Core Questionnaire',
      description: 'Review the standard questionnaire workflow for clients.',
      videoSrc: '/videos/knowledgebase/core-questionnaire.mp4',
    },
    {
      badge: 'Questionnaires',
      title: 'Custom Questionnaire',
      description: 'Create and manage custom questionnaires.',
      videoSrc: '/videos/knowledgebase/custom-questionnaire.mp4',
    },
    {
      badge: 'Appointments',
      title: 'Appointments',
      description: 'Schedule, reschedule, and manage appointment workflows.',
      videoSrc: '/videos/knowledgebase/appointments.mp4',
    },
    {
      badge: 'Meal Planning',
      title: 'AI Meal Plan',
      description: 'Generate and review AI-assisted meal plans.',
      videoSrc: '/videos/knowledgebase/ai-meal-plan.mp4',
    },
    {
      badge: 'Meal Planning',
      title: 'Manual Meal Planning',
      description: 'Create and manage manual meal plans.',
      videoSrc: '/videos/knowledgebase/manual-meal-plan.mp4',
    },
    {
      badge: 'Recipes',
      title: 'Custom Recipe',
      description: 'Create and manage custom recipes in your recipe library.',
      videoSrc: '/videos/knowledgebase/custom-recipe.mp4',
    },
    {
      badge: 'Templates',
      title: 'Templates',
      description: 'Save, reuse, and manage meal planning templates.',
      videoSrc: '/videos/knowledgebase/templates.mp4',
    },
    {
      badge: 'Client Records',
      title: 'Lab Reports',
      description: 'Upload and review client lab reports.',
      videoSrc: '/videos/knowledgebase/lab-reports.mp4',
    },
  ];

  const handleToggleFaqItem = (index: number) => {
    setFaqItems((prevItems) =>
      prevItems.map((item, itemIndex) =>
        itemIndex === index ? { ...item, expanded: !item.expanded } : item,
      ),
    );
  };

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <Box
          sx={{
            backgroundColor: '#fbfbfb',
            p: 2,
            '@media (min-width:600px)': { p: 2.5 },
            '@media (min-width:1200px)': { px: 3.5, pt: 3, pb: 3.5 },
          }}
        >
          <Stack spacing={3}>
            <HelpSupportHeader />
            <HelpSupportTabs value={activeTab} onChange={setActiveTab} />
            {activeTab === 0 ? (
              <HelpSupportProductGuide items={guideCards} />
            ) : (
              <Grid container spacing={3.5}>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <HelpSupportFaqCard items={faqItems} onToggle={handleToggleFaqItem} />
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <HelpSupportContactCard />
                </Grid>
              </Grid>
            )}
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default KnowledgebasePage;
